import { FC, ReactNode } from 'react';
import { ShieldTickIcon } from '../../../assets/icons/ShieldTickIcon';
import { parseText } from '../../../utils/string/parseText';
import { LocaleData } from '../../../models/locale/locale-data';

export const styles = {
  inner: 'w-11/12 md:max-w-[75%] mx-auto grid grid-cols-12  *:col-span-12',
  banner: {
    container: 'col-span-12 ticker bg-(--color-banner-bg-ticker)',
    // text-h6 to match the live Haier ticker weight (the other brands' live tickers
    // use text-body-large; Haier's is heavier — this is the only brand that differs).
    title: 'text-h6 headline my-space-200 text-center text-(--color-banner-text-ticker)',
  },
  hero: {
    container:
      'col-span-12 flex justify-center items-center gap-spacing-400 text-center bg-(--color-banner-bg-hero)',
    number: 'text-display-jumbotron text-(--color-banner-text-hero)',
    subtitle: 'text-h1 font-bold w-full text-(--color-banner-text-hero)',
  },
  cta: {
    container: 'col-span-12 flex justify-center cta-container',
    layout: 'text-center flex flex-col items-center py-space-600 gap-space-500 ',
  },
  registration: {
    title: 'text-h2 headline text-center',
  },
  benefits: {
    benefit:
      'col-span-12 md:col-span-3 flex flex-col items-center px-space-600 mb-space-500 md:mb-0',
  },
  asteriskContent: {
    container:
      'col-span-12 flex flex-col gap-space-500 text-center  border-t border-solid border-neutral-200',
    text: 'body-text-small mb-space-200',
  },
};

export const BannerSection: FC<{ text: string }> = ({ text }) => (
  <article className={styles.banner.container}>
    <div className={styles.inner}>
      <p className={styles.banner.title}>{text}</p>
    </div>
  </article>
);

export const HeroSection: FC<{ number: string; copy: string }> = ({ number, copy }) => (
  <article className={styles.hero.container}>
    <div className={styles.inner}>
      <div className="col-span-12 flex flex-col items-center gap-space-300 py-space-200">
        <p className={styles.hero.number}>{number}</p>
        <h1 className={styles.hero.subtitle}>{copy}</h1>
      </div>
    </div>
  </article>
);

export const PromoSection: FC<{ subCopy1: string; subCopy2: string }> = ({
  subCopy1,
  subCopy2,
}) => (
  <article className="bg-(--color-banner-bg-promotion) col-span-12">
    <div className={styles.inner}>
      <p className="col-span-12 text-center text-balance pt-space-300 mb-space-300 px-space-500 text-(--color-banner-text-promotion)">
        {parseText(subCopy1)}
        <br />
        {subCopy2}
      </p>
    </div>
  </article>
);

/** Wraps a variant's CTA content in the shared CTA layout with its heading. */
export const CtaSection: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <article className={styles.cta.container}>
    <div className={styles.inner}>
      <div className={styles.cta.layout}>
        <h2 className={styles.registration.title}>{title}</h2>
        {children}
      </div>
    </div>
  </article>
);

export const BenefitsSection: FC<{ header: string; benefits: string[] }> = ({
  header,
  benefits,
}) => (
  <article className="col-span-12 bg-neutral py-space-700 px-space-400">
    <div className={styles.inner}>
      <h3 className="col-span-12 text-h2 py-space-600 text-center">{header}</h3>
      {benefits.map((benefit, index) => (
        <div key={index} className={styles.benefits.benefit}>
          <span className="text-(--color-icon-list-decorative)">
            <ShieldTickIcon />
          </span>
          <p className="mt-space-200 text-center">{benefit}</p>
        </div>
      ))}
    </div>
  </article>
);

export const SmallPrintSection: FC<{ data: LocaleData }> = ({ data }) => (
  <article className={styles.asteriskContent.container}>
    <div className={`${styles.inner} my-space-700`}>
      <p className={styles.asteriskContent.text}>
        {parseText(data?.heroAsterix?.[0] ?? '', data?.heroAsterixLinks ?? [])}
      </p>
      <p className={styles.asteriskContent.text}>
        {parseText(data?.phoneNumberAsterix?.[0] ?? '', data?.phoneNumberAsterixLinks ?? [])}
      </p>
    </div>
  </article>
);
